/**
 * ClockCamera / ClockReceipt — screens 2 and 3 of "Facial Clock-In Flow".
 * @file src/web/features/clock/components/ClockCamera.tsx
 *
 * @llm-rule WHEN: Someone has tapped the clock-in (or clock-out) action
 * @llm-rule NOTE: The camera is a deliberate step now, not the landing page —
 *   it opens on a tap, has a visible way out, and hands back to the dashboard
 *   through a receipt that says what was recorded.
 */

import type { ReactNode } from 'react';
import { Button } from '@bloomneo/uikit';
import { Check, Loader2, X } from 'lucide-react';
import { FaceScanner, type FaceScannerHandle } from '../../face/components/FaceScanner';

/* -------------------------------------------------------------------------- */
/* Screen 2 — the camera                                                      */
/* -------------------------------------------------------------------------- */

export function ClockCamera({
  scannerRef,
  now,
  title,
  caption,
  actionLabel,
  timingNote,
  busy,
  onCancel,
  onCapture,
  children,
}: {
  scannerRef: React.Ref<FaceScannerHandle>;
  now: Date;
  title: string;
  caption: string;
  actionLabel: string;
  /** "will log as on time" / "will log as late", decided by the caller. */
  timingNote: string;
  busy: boolean;
  onCancel: () => void;
  onCapture: () => void;
  /** Anything extra between the camera and the button — the sign-off note. */
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="label-mono text-ink-ghost">Live camera</div>
          <h1 className="mt-0.5 truncate text-[20px] font-semibold tracking-[-0.02em] text-ink">
            {title}
          </h1>
        </div>

        <button
          type="button"
          onClick={onCancel}
          aria-label="Close the camera"
          className="grid size-9 flex-none place-items-center rounded-full border border-rule bg-paper text-ink-soft transition-colors hover:bg-paper-sunk"
        >
          <X className="size-[18px]" strokeWidth={2.2} />
        </button>
      </div>

      {/*
        The oval and corner brackets from the wireframe sit OVER the video and
        must not eat the tap — pointer-events-none, or the guide would swallow
        clicks meant for the camera surface.
      */}
      <div className="relative overflow-hidden rounded-[20px]">
        <FaceScanner ref={scannerRef} caption={caption} height={280} />

        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-[50%/45%] border-2 border-white/70"
            style={{ width: '52%', height: '74%' }}
          />
          {/* Viewfinder corners, in the brand rather than the sketch orange. */}
          <svg className="absolute inset-0 size-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <g
              fill="none"
              stroke="var(--brand-2)"
              strokeWidth="0.8"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            >
              <polyline points="10,22 10,10 24,10" />
              <polyline points="90,22 90,10 76,10" />
              <polyline points="10,78 10,90 24,90" />
              <polyline points="90,78 90,90 76,90" />
            </g>
          </svg>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-clock text-[26px] font-bold leading-none text-ink">
          {now.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })}
        </span>
        <span className="text-[13.5px] text-ink-soft">{timingNote}</span>
      </div>

      {children}

      <Button
        onClick={onCapture}
        disabled={busy}
        className="h-14 w-full rounded-[14px] text-[16px] font-semibold"
        style={{ background: 'var(--brand-ramp)', color: '#fff', boxShadow: 'var(--elev-2)' }}
      >
        {busy ? (
          <>
            <Loader2 className="mr-2 size-4 animate-spin" />
            Matching your face…
          </>
        ) : (
          actionLabel
        )}
      </Button>

      <button
        type="button"
        onClick={onCancel}
        className="text-center text-[13px] text-ink-faint underline underline-offset-4 hover:text-ink-soft"
      >
        Not now — back to dashboard
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Screen 3 — the receipt                                                     */
/* -------------------------------------------------------------------------- */

export function ClockReceipt({
  title,
  time,
  rows,
  note,
  onDone,
}: {
  title: string;
  time: string;
  rows: Array<{ label: string; value: ReactNode }>;
  note?: string;
  onDone: () => void;
}) {
  return (
    <div className="anim-rise flex flex-col items-center gap-4 py-2">
      <div
        className="grid size-24 place-items-center rounded-full text-white"
        style={{ background: 'var(--brand-ramp)', boxShadow: 'var(--elev-2)' }}
      >
        <Check className="size-11" strokeWidth={2.4} />
      </div>

      <div className="text-center">
        <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink">{title}</h1>
        <div className="font-clock mt-1 text-[32px] font-bold leading-none text-ink">{time}</div>
      </div>

      <div className="w-full rounded-[16px] border border-rule bg-paper p-4 elev-1">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline justify-between gap-3 border-b border-dashed border-rule py-2 text-[13.5px] last:border-0"
          >
            <span className="text-ink-faint">{row.label}</span>
            <span className="text-right font-medium text-ink">{row.value}</span>
          </div>
        ))}
      </div>

      {note && (
        <p className="max-w-[42ch] text-center text-[13px] leading-relaxed text-ink-soft">{note}</p>
      )}

      <Button variant="outline" onClick={onDone} className="h-12 w-full rounded-[14px] text-[15px]">
        Back to dashboard
      </Button>
    </div>
  );
}
